import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getMatch, exportCsvUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Activity, Download, Image as ImageIcon } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";

export default function MatchView() {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => { getMatch(id).then(setMatch); }, [id]);

  const downloadImage = async () => {
    if (!cardRef.current) return;
    try {
      setExporting(true);
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0A0A0A",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const filename = `scorecard-${id.slice(0, 8)}.png`;

      // Try Web Share API with a File first
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
      if (blob && navigator.canShare) {
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: "CrickPad Scorecard",
              text: match?.result || `${match?.teams?.[0]?.name} vs ${match?.teams?.[1]?.name}`,
              files: [file],
            });
            toast.success("Shared");
            return;
          } catch (err) {
            if (err?.name === "AbortError") return; // user cancelled
            // fall through to download
          }
        }
      }

      // Fallback: download
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      a.click();
      toast.success("Scorecard image downloaded");
    } catch (e) {
      toast.error("Failed to generate image");
    } finally {
      setExporting(false);
    }
  };

  if (!match) return <div className="p-12 text-center text-muted-foreground uppercase tracking-[0.3em] text-xs">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="match-view-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <Link to="/history" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={exportCsvUrl(id)}
            data-testid="export-csv-btn"
            className="tactical-btn border border-border hover:border-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
          >
            <Download className="w-3 h-3" strokeWidth={3} /> CSV
          </a>
          <button
            onClick={downloadImage}
            disabled={exporting}
            data-testid="export-image-btn"
            className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <ImageIcon className="w-3 h-3" strokeWidth={3} /> {exporting ? "Rendering..." : "Share Image"}
          </button>
        </div>
      </div>

      <div ref={cardRef} className="bg-background pt-2">
      <div className="border border-border bg-card p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">
            {match.format} · {match.overs} ov · {match.venue || "—"}
          </span>
          {match.status === "live" ? (
            <Link to={`/match/${match.id}/score`} className="text-xs uppercase tracking-[0.2em] text-destructive font-bold inline-flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Live
            </Link>
          ) : (
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Final</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-6">
          {match.innings.map((inn, idx) => (
            <div key={idx}>
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{inn.batting_team_name}</div>
              <div className="flex items-baseline gap-3">
                <span className="font-display font-black text-4xl sm:text-5xl stat-tabular">{inn.summary.runs}/{inn.summary.wickets}</span>
                <span className="font-mono text-sm text-muted-foreground stat-tabular">({inn.summary.overs})</span>
              </div>
            </div>
          ))}
        </div>
        {match.result && (
          <div className="mt-5 pt-5 border-t border-border">
            <p className="font-display font-black text-xl text-primary uppercase">{match.result}</p>
          </div>
        )}
      </div>

      <Tabs defaultValue="0" className="w-full">
        <TabsList className="bg-transparent border-b border-border w-full justify-start rounded-none p-0">
          {match.innings.map((inn, idx) => (
            <TabsTrigger
              key={idx}
              value={String(idx)}
              data-testid={`innings-tab-${idx}`}
              className="font-display uppercase tracking-tight data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3"
            >
              Inn {idx + 1} · {inn.batting_team_name}
            </TabsTrigger>
          ))}
        </TabsList>

        {match.innings.map((inn, idx) => (
          <TabsContent key={idx} value={String(idx)} className="mt-6 space-y-6">
            {/* Batting */}
            <div className="border border-border bg-card">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h3 className="font-display font-black uppercase tracking-tight text-lg">Batting</h3>
                <span className="font-mono text-sm stat-tabular">{inn.summary.runs}/{inn.summary.wickets} ({inn.summary.overs})</span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-left px-5 py-2 font-bold">Batsman</th>
                    <th className="text-right px-3 py-2 font-bold">R</th>
                    <th className="text-right px-3 py-2 font-bold">B</th>
                    <th className="text-right px-3 py-2 font-bold">4s</th>
                    <th className="text-right px-3 py-2 font-bold">6s</th>
                    <th className="text-right px-5 py-2 font-bold">SR</th>
                  </tr>
                </thead>
                <tbody>
                  {inn.batsmen_stats.map((b, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-card" : "bg-[#0F1410]"} border-t border-border`}>
                      <td className="px-5 py-2 font-display uppercase font-bold">
                        {b.name}
                        {b.did_not_bat ? <span className="text-muted-foreground text-xs ml-2 normal-case font-normal">did not bat</span> :
                          !b.out ? <span className="text-primary text-xs ml-2 normal-case font-normal">not out</span> : null}
                      </td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.runs}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.balls}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.fours}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.sixes}</td>
                      <td className="px-5 py-2 text-right font-mono stat-tabular">{b.strike_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-2 border-t border-border text-xs text-muted-foreground font-mono">
                Extras: <span className="text-foreground stat-tabular">{inn.summary.extras}</span>
              </div>
            </div>

            {/* Bowling */}
            <div className="border border-border bg-card">
              <div className="px-5 py-3 border-b border-border">
                <h3 className="font-display font-black uppercase tracking-tight text-lg">Bowling</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-left px-5 py-2 font-bold">Bowler</th>
                    <th className="text-right px-3 py-2 font-bold">O</th>
                    <th className="text-right px-3 py-2 font-bold">R</th>
                    <th className="text-right px-3 py-2 font-bold">W</th>
                    <th className="text-right px-5 py-2 font-bold">ECO</th>
                  </tr>
                </thead>
                <tbody>
                  {inn.bowler_stats.map((b, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-card" : "bg-[#0F1410]"} border-t border-border`}>
                      <td className="px-5 py-2 font-display uppercase font-bold">{b.name}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.overs}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.runs}</td>
                      <td className="px-3 py-2 text-right font-mono stat-tabular">{b.wickets}</td>
                      <td className="px-5 py-2 text-right font-mono stat-tabular">{b.economy}</td>
                    </tr>
                  ))}
                  {inn.bowler_stats.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-4 text-center text-muted-foreground text-xs italic">No deliveries bowled yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>
      </div>
    </div>
  );
}
